import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest } from '../../services/api';
import DashboardLayout from '../../components/Dashboard/DashboardLayout';
import ClassSelector from '../../components/ClassSelector';
import useClassContext from '../../hooks/useClassContext';
import { teacherNavItems } from '../../constants/navigation';

const TABS = [
  { id: 'profile', label: 'Profile' },
  { id: 'password', label: 'Password' },
  { id: 'ai_preferences', label: 'AI Preferences' },
];

const SettingsPage = () => {
  const { currentUser } = useAuth();
  const { selectedClassId, setSelectedClassId, appendClassFilter } = useClassContext();
  const [activeTab, setActiveTab] = useState('profile');

  return (
    <DashboardLayout title="Settings" navItems={teacherNavItems} userRole="teacher" userName={currentUser?.full_name || 'Teacher'}>
      <h2 className="text-3xl font-bold text-gray-900 mb-6">Settings</h2>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <div className="flex gap-4">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'profile' && <ProfileTab />}
      {activeTab === 'password' && <PasswordTab />}
      {activeTab === 'ai_preferences' && (
        <AIPreferencesTab
          selectedClassId={selectedClassId}
          setSelectedClassId={setSelectedClassId}
          appendClassFilter={appendClassFilter}
        />
      )}
    </DashboardLayout>
  );
};

// ============================================================================
// PROFILE TAB
// ============================================================================
const ProfileTab = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState({ full_name: '', email: '', phone: '' });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const data = await apiRequest('/api/teacher/profile', 'GET');
        setProfile(data);
        setForm({ full_name: data.full_name || '', email: data.email || '', phone: data.phone || '' });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await apiRequest('/api/teacher/profile', 'PUT', form);
      setSuccess('Profile updated successfully');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-2xl">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Profile Information</h3>

      {error && <Alert type="error" message={error} onDismiss={() => setError(null)} />}
      {success && <Alert type="success" message={success} onDismiss={() => setSuccess(null)} />}

      <form onSubmit={handleSave} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
          <input
            type="text"
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
          <input
            type="text"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Optional"
          />
        </div>

        {/* Read-only fields */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
          <input
            type="text"
            value={profile?.subject || ''}
            disabled
            className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">School</label>
          <input
            type="text"
            value={profile?.school_name || ''}
            disabled
            className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
          />
        </div>

        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </form>
    </div>
  );
};

// ============================================================================
// PASSWORD TAB
// ============================================================================
const PasswordTab = () => {
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const getStrength = (password) => {
    if (!password) return { level: 0, label: '' };
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++;

    if (score <= 2) return { level: 1, label: 'Weak', color: 'bg-red-500' };
    if (score <= 3) return { level: 2, label: 'Fair', color: 'bg-yellow-500' };
    if (score <= 4) return { level: 3, label: 'Good', color: 'bg-blue-500' };
    return { level: 4, label: 'Strong', color: 'bg-green-500' };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (form.new_password !== form.confirm_password) {
      setError('Passwords do not match');
      return;
    }

    try {
      setSaving(true);
      await apiRequest('/api/teacher/password', 'PUT', {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      setSuccess('Password changed successfully');
      setForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const strength = getStrength(form.new_password);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-2xl">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Change Password</h3>

      {error && <Alert type="error" message={error} onDismiss={() => setError(null)} />}
      {success && <Alert type="success" message={success} onDismiss={() => setSuccess(null)} />}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
          <input
            type="password"
            value={form.current_password}
            onChange={(e) => setForm({ ...form, current_password: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
          <input
            type="password"
            value={form.new_password}
            onChange={(e) => setForm({ ...form, new_password: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            required
          />
          {/* Strength indicator */}
          {form.new_password && (
            <div className="mt-2">
              <div className="flex gap-1 mb-1">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className={`h-1 flex-1 rounded-full ${i <= strength.level ? strength.color : 'bg-gray-200'}`} />
                ))}
              </div>
              <p className="text-xs text-gray-500">{strength.label} - Requires 8+ chars, uppercase, lowercase, number, special char</p>
            </div>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
          <input
            type="password"
            value={form.confirm_password}
            onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            required
          />
          {form.confirm_password && form.new_password !== form.confirm_password && (
            <p className="text-xs text-red-600 mt-1">Passwords do not match</p>
          )}
        </div>

        <button
          type="submit"
          disabled={saving || !form.current_password || !form.new_password || form.new_password !== form.confirm_password}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium"
        >
          {saving ? 'Changing...' : 'Change Password'}
        </button>
      </form>
    </div>
  );
};

// ============================================================================
// AI PREFERENCES TAB
// ============================================================================
const AIPreferencesTab = ({ selectedClassId, setSelectedClassId, appendClassFilter }) => {
  const [classSections, setClassSections] = useState([]);
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Fetch class sections
  useEffect(() => {
    const fetchSections = async () => {
      try {
        const dashData = await apiRequest(appendClassFilter('/api/teacher/dashboard'), 'GET');
        if (dashData.class_sections) setClassSections(dashData.class_sections);
      } catch (err) {
        // Non-critical
      }
    };
    fetchSections();
  }, [appendClassFilter]);

  // Fetch preferences for selected class
  const fetchPrefs = useCallback(async () => {
    if (!selectedClassId) {
      setPrefs(null);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await apiRequest(`/api/teacher/classes/${selectedClassId}/preferences`, 'GET');
      setPrefs(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedClassId]);

  useEffect(() => {
    fetchPrefs();
  }, [fetchPrefs]);

  const handleSave = async () => {
    if (!selectedClassId || !prefs) return;
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await apiRequest(`/api/teacher/classes/${selectedClassId}/preferences`, 'PUT', prefs);
      setSuccess('AI preferences saved');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-2xl">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">AI Preferences</h3>
      <p className="text-sm text-gray-600 mb-4">Customize how the AI tutor behaves for each class.</p>

      {classSections.length > 0 && (
        <ClassSelector
          classSections={classSections}
          selectedClassId={selectedClassId}
          onClassChange={setSelectedClassId}
        />
      )}

      {!selectedClassId && (
        <div className="text-center py-8 text-gray-500">
          Select a class section to configure AI preferences.
        </div>
      )}

      {selectedClassId && loading && <LoadingSpinner />}

      {error && <Alert type="error" message={error} onDismiss={() => setError(null)} />}
      {success && <Alert type="success" message={success} onDismiss={() => setSuccess(null)} />}

      {selectedClassId && prefs && !loading && (
        <div className="space-y-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Custom Instruction Prompt</label>
            <textarea
              value={prefs.custom_prompt || ''}
              onChange={(e) => setPrefs({ ...prefs, custom_prompt: e.target.value })}
              maxLength={500}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              placeholder="e.g., Focus on visual explanations for geometry"
            />
            <p className="text-xs text-gray-500 mt-1">{(prefs.custom_prompt || '').length}/500</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Socratic Intensity</label>
              <select
                value={prefs.socratic_intensity || 'moderate'}
                onChange={(e) => setPrefs({ ...prefs, socratic_intensity: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="light">Light</option>
                <option value="moderate">Moderate</option>
                <option value="heavy">Heavy</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hint Level</label>
              <select
                value={prefs.hint_level || 'moderate'}
                onChange={(e) => setPrefs({ ...prefs, hint_level: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="subtle">Subtle</option>
                <option value="moderate">Moderate</option>
                <option value="direct">Direct</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Response Length</label>
              <select
                value={prefs.response_length || 'standard'}
                onChange={(e) => setPrefs({ ...prefs, response_length: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="concise">Concise</option>
                <option value="standard">Standard</option>
                <option value="detailed">Detailed</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Current Unit</label>
            <input
              type="text"
              value={prefs.current_unit || ''}
              onChange={(e) => setPrefs({ ...prefs, current_unit: e.target.value })}
              maxLength={200}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              placeholder="e.g., Chapter 5: Fractions"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Focus Topics (comma-separated)</label>
            <input
              type="text"
              value={prefs.focus_topics || ''}
              onChange={(e) => setPrefs({ ...prefs, focus_topics: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              placeholder="e.g., fractions, decimals, mixed numbers"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Blackout Dates (comma-separated)</label>
            <input
              type="text"
              value={prefs.blackout_dates || ''}
              onChange={(e) => setPrefs({ ...prefs, blackout_dates: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              placeholder="e.g., 2026-03-15, 2026-03-22"
            />
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium"
          >
            {saving ? 'Saving...' : 'Save Preferences'}
          </button>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// SHARED COMPONENTS
// ============================================================================
const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-32">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
    <span className="ml-3 text-gray-600">Loading...</span>
  </div>
);

const Alert = ({ type, message, onDismiss }) => {
  const styles = type === 'error'
    ? 'bg-red-50 border-red-200 text-red-700'
    : 'bg-green-50 border-green-200 text-green-700';

  return (
    <div className={`border rounded-lg p-3 mb-4 flex justify-between items-center ${styles}`}>
      <span className="text-sm">{message}</span>
      {onDismiss && (
        <button onClick={onDismiss} className="text-sm opacity-60 hover:opacity-100 ml-2">&times;</button>
      )}
    </div>
  );
};

export default SettingsPage;
